import React, { useEffect } from 'react';
import { View, Modal, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';

import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { trackAuthEvent } from '../../services/analytics';
import { brand } from '../../design-system/theme/tokens';

const PAYWALL_TRIGGER = 'trial_expired';

const COLORS = {
  primary: brand.primary,
  primaryLight: '#3B82F6',
  warning: '#F59E0B',
  success: '#10B981',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

interface TrialExpiredModalProps {
  visible: boolean;
  onDismiss: () => void;
  overallLevel?: string;
}

export default function TrialExpiredModal({ visible, onDismiss, overallLevel }: TrialExpiredModalProps) {
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    if (visible) {
      trackAuthEvent('trial_paywall_viewed', { trigger: PAYWALL_TRIGGER, overall_level: overallLevel ?? null });
    }
  }, [visible, overallLevel]);

  const handleSubscribe = () => {
    trackAuthEvent('trial_paywall_cta_clicked', { trigger: PAYWALL_TRIGGER, destination: 'gated_plan_selection' });
    onDismiss();
    router.push(ROUTES.GATED_PLAN_SELECTION);
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss}>
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <View className="mx-6 rounded-2xl bg-white overflow-hidden w-full max-w-sm" style={{ maxWidth: 380 }}>
          {/* Warning header */}
          <LinearGradient
            colors={[COLORS.warning, '#FBBF24']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ paddingVertical: 14, paddingHorizontal: 20, alignItems: 'center', flexDirection: 'row', justifyContent: 'center' }}
          >
            <MaterialIcons name="timer-off" size={18} color="white" />
            <Text className="text-white font-bold ml-2 text-sm">
              {t('trial.expired.headerBadge') || 'Trial period ended'}
            </Text>
          </LinearGradient>

          <View className="p-6">
            <View className="items-center mb-5">
              {/* Level teaser if available */}
              {overallLevel && (
                <View className="flex-row items-center px-4 py-2 rounded-full mb-3" style={{ backgroundColor: `${COLORS.primary}08`, borderWidth: 1, borderColor: `${COLORS.primary}20` }}>
                  <MaterialIcons name="emoji-events" size={18} color={COLORS.primary} />
                  <Text className="ml-2 font-bold" style={{ color: COLORS.primary }}>
                    {overallLevel}
                  </Text>
                </View>
              )}

              <Text className="text-xl font-bold text-center mb-2" style={{ color: COLORS.text.primary }}>
                {t('trial.expired.modalTitle') || "Don't lose your analysis"}
              </Text>

              <Text className="text-sm text-center leading-5" style={{ color: COLORS.text.secondary }}>
                {t('trial.expired.modalDescription') ||
                  'Your coaching analysis and AI conversations are waiting. Subscribe to keep them forever and unlock unlimited access.'}
              </Text>
            </View>

            {/* Loss aversion: what they lose vs keep */}
            <View className="rounded-xl overflow-hidden mb-5">
              <View className="p-3" style={{ backgroundColor: '#FEF2F2' }}>
                <View className="flex-row items-center">
                  <MaterialIcons name="close" size={16} color="#DC2626" />
                  <Text className="ml-2 text-sm font-medium" style={{ color: '#DC2626' }}>
                    {t('trial.expired.losing') || 'Without subscribing'}
                  </Text>
                </View>
                <Text className="text-xs mt-1 ml-6" style={{ color: '#991B1B' }}>
                  {t('trial.expired.losingDetail') || 'Analysis results locked. Chat history inaccessible.'}
                </Text>
              </View>
              <View className="p-3" style={{ backgroundColor: '#F0FDF4' }}>
                <View className="flex-row items-center">
                  <MaterialIcons name="check" size={16} color="#16A34A" />
                  <Text className="ml-2 text-sm font-medium" style={{ color: '#16A34A' }}>
                    {t('trial.expired.keeping') || 'With subscription'}
                  </Text>
                </View>
                <Text className="text-xs mt-1 ml-6" style={{ color: '#166534' }}>
                  {t('trial.expired.keepingDetail') || 'All results unlocked forever. Unlimited analyses and conversations.'}
                </Text>
              </View>
            </View>

            {/* Launch offer */}
            <View className="mb-3 rounded-lg px-3 py-2" style={{ backgroundColor: '#FEF3C7', borderWidth: 1, borderColor: '#FCD34D' }}>
              <Text className="text-xs font-semibold text-center" style={{ color: '#92400E' }}>
                {`🎉 ${t('trial.launchOffer') || 'Launch offer: 20% off your first 3 months — use code LAUNCH20 at checkout'}`}
              </Text>
            </View>

            {/* CTA */}
            <TouchableOpacity onPress={handleSubscribe} className="w-full rounded-xl overflow-hidden mb-1" activeOpacity={0.85}>
              <LinearGradient
                colors={[COLORS.primary, '#2563EB']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{ paddingVertical: 14, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
              >
                <MaterialIcons name="lock-open" size={18} color="white" />
                <Text className="text-white font-bold ml-2 text-base">
                  {t('trial.expired.subscribeCTA') || 'Unlock My Results'}
                </Text>
              </LinearGradient>
            </TouchableOpacity>

            <Text className="text-xs text-center mt-1 mb-2" style={{ color: COLORS.text.muted }}>
              {t('trial.expired.guarantee') || 'Cancel anytime. Results stay forever.'}
            </Text>

            <TouchableOpacity onPress={onDismiss} className="py-2">
              <Text className="text-center text-sm" style={{ color: COLORS.text.muted }}>
                {t('trial.expired.dismiss') || 'Maybe later'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}
